import { type AudienceFact } from '@bbkAdminComponents/experiences/audiences/edit-rule/edit-rule-types';

/** Chargebee identifier facts whose audience values are trimmed on save. */
export const OPTIMIZER_AUDIENCE_IDENTIFIER_FACTS = new Set([
  'subscription_id',
  'customer_id',
  'coupon_id',
  'plan_id',
  'product_family_id',
]);

const hasSurroundingWhitespace = (value: unknown): boolean =>
  typeof value === 'string'
    ? value !== value.trim()
    : Array.isArray(value) &&
      value.some((item) => typeof item === 'string' && item !== item.trim());

export const hasOptimizerAudienceIdentifierWhitespace = (
  node: unknown
): boolean => {
  if (node === null || typeof node !== 'object') {
    return false;
  }

  const record = node as Record<string, unknown>;
  if (
    typeof record.fact === 'string' &&
    OPTIMIZER_AUDIENCE_IDENTIFIER_FACTS.has(record.fact) &&
    hasSurroundingWhitespace(record.value)
  ) {
    return true;
  }

  return Object.values(record).some(hasOptimizerAudienceIdentifierWhitespace);
};

const optimizerAudiencePropertyLabels = {
  status: 'Subscription status',
  subscription_id: 'Subscription ID',
  customer_id: 'Customer ID',
  coupon_id: 'Coupon ID',
  plan_interval: 'Billing frequency',
  current_term_start: 'Current term start date',
  current_term_end: 'Current term end date',
  quantity: 'Subscription item quantity',
  plan_amount: 'Plan amount',
  item_amount: 'Item amount',
  unit: 'Unit value',
  plan_interval_length: 'Billing frequency length',
  plan_id: 'Plan',
  product_family_id: 'Product family',
  has_scheduled_changes: 'Has scheduled changes',
  has_scheduled_advance_invoices: 'Has scheduled advance invoices',
  has_contract_term: 'Has active contract term',
  has_coupon: 'Has coupon',
} as const;

type OptimizerAudiencePropertyKey =
  keyof typeof optimizerAudiencePropertyLabels;

const normalizeFactName = (fact: string): string =>
  fact.replace(/^(custom|standard|reserved|alert|ai)\./, '');

export const formatOptimizerAudiencePropertyName = (fact: string): string => {
  const normalizedFact = normalizeFactName(fact);
  return (
    optimizerAudiencePropertyLabels[
      normalizedFact as OptimizerAudiencePropertyKey
    ] ?? normalizedFact
  );
};

export const getOptimizerAudienceProperties = (
  billingIntegrationId?: number
): AudienceFact[] => {
  return [
    {
      name: 'Subscription status',
      val: 'status',
      type: 'text',
      is_lov: true,
      field_name: 'subscription.status',
      integration_id: billingIntegrationId,
    },
    {
      name: 'Subscription ID',
      val: 'subscription_id',
      type: 'text',
    },
    {
      name: 'Customer ID',
      val: 'customer_id',
      type: 'text',
    },
    {
      name: 'Coupon ID',
      val: 'coupon_id',
      type: 'text',
    },
    {
      name: 'Has coupon',
      val: 'has_coupon',
      type: 'boolean',
    },
    {
      name: 'Billing frequency',
      val: 'plan_interval',
      type: 'text',
      is_lov: true,
      field_name: 'subscription.billing_period_unit',
      integration_id: billingIntegrationId,
    },
    {
      name: 'Current term start date',
      val: 'current_term_start',
      type: 'date',
    },
    {
      name: 'Current term end date',
      val: 'current_term_end',
      type: 'date',
    },
    {
      name: 'Subscription item quantity',
      val: 'quantity',
      type: 'integer',
    },
    {
      name: 'Plan amount',
      val: 'plan_amount',
      type: 'integer',
    },
    {
      name: 'Unit value',
      val: 'unit',
      type: 'integer',
    },
    {
      name: 'Billing frequency length',
      val: 'plan_interval_length',
      type: 'integer',
    },
    {
      name: 'Plan',
      val: 'plan_id',
      type: 'text',
      is_lov: true,
      field_name: 'subscription.plan_id',
      integration_id: billingIntegrationId,
    },
    {
      name: 'Product family',
      val: 'product_family_id',
      type: 'text',
      is_lov: true,
      field_name: 'subscription.product_family_id',
      integration_id: billingIntegrationId,
    },
    {
      name: 'Has scheduled changes',
      val: 'has_scheduled_changes',
      type: 'boolean',
    },
    {
      name: 'Has scheduled advance invoices',
      val: 'has_scheduled_advance_invoices',
      type: 'boolean',
    },
    {
      name: 'Has active contract term',
      val: 'has_contract_term',
      type: 'boolean',
    },
  ];
};
